import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Field, NativeSelect } from "./Common";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const METHODS = ["card", "cash", "bank_transfer", "sumup", "stripe", "other"];
const BLANK_LINE = { description: "", qty: 1, unit_price: "", tax_rate: "", tax_note: "" };

function LineRow({ l, i, onChange, onRemove }) {
  const set = (k) => (e) => onChange(i, { ...l, [k]: e.target.value });
  return (
    <div className="grid grid-cols-[1fr_60px_90px_70px_auto] gap-2 items-center" data-testid={`inv-line-${i}`}>
      <Input placeholder="Description" data-testid={`inv-line-desc-${i}`} value={l.description} onChange={set("description")} />
      <Input type="number" min={1} data-testid={`inv-line-qty-${i}`} value={l.qty} onChange={set("qty")} />
      <Input type="number" step="0.01" placeholder="Price incl. VAT" data-testid={`inv-line-price-${i}`} value={l.unit_price} onChange={set("unit_price")} />
      <Input type="number" step="0.1" placeholder="VAT %" data-testid={`inv-line-vat-${i}`} value={l.tax_rate} onChange={set("tax_rate")} />
      <button type="button" onClick={() => onRemove(i)} data-testid={`inv-line-remove-${i}`}><Trash2 className="h-4 w-4 text-muted-foreground" /></button>
    </div>
  );
}

export function InvoiceDialog({ open, onOpenChange, onDone }) {
  const { locations, locationId } = useApp();
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [f, setF] = useState(null);
  useEffect(() => {
    if (open) setF({ location_id: locationId || locations[0]?.id || "", client_id: "", buyer: { name: "", email: "", address: "", tax_code: "" }, lines: [{ ...BLANK_LINE }], notes: "", paid: true, method: "card" });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (q.length >= 2) api.get("/clients", { params: { q } }).then((r) => setClients((r.data.items || r.data).slice(0, 6))).catch(() => {});
  }, [q]);
  if (!f) return null;
  const setLine = (i, l) => setF({ ...f, lines: f.lines.map((x, j) => (j === i ? l : x)) });
  const pick = (c) => { setF({ ...f, client_id: c.id, buyer: { name: `${c.first_name} ${c.last_name}`.trim(), email: c.email || "", address: c.address || "", tax_code: c.tax_code || "" } }); setQ(""); setClients([]); };
  const total = f.lines.reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.unit_price) || 0), 0);
  const save = async () => {
    try {
      const body = { ...f, client_id: f.client_id || null, lines: f.lines.filter((l) => l.description).map((l) => ({ ...l, qty: Number(l.qty) || 1, unit_price: Number(l.unit_price) || 0, tax_rate: Number(l.tax_rate) || 0 })) };
      const { data } = await api.post("/invoices", body);
      toast.success(`Invoice ${data.number} created`); onOpenChange(false); onDone?.();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="invoice-dialog">
        <DialogHeader><DialogTitle>New invoice</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Location"><NativeSelect testId="inv-location" value={f.location_id} onChange={(v) => setF({ ...f, location_id: v })} options={locations.map((l) => ({ value: l.id, label: `${l.name} (${l.currency})` }))} /></Field>
          <Field label="Find patient (optional)">
            <Input data-testid="inv-client-search" placeholder="Type a name" value={q} onChange={(e) => setQ(e.target.value)} />
            {clients.length > 0 && <div className="border rounded-md mt-1 divide-y bg-card">{clients.map((c) => <button key={c.id} type="button" className="block w-full text-left px-3 py-2 text-sm hover:bg-muted" data-testid={`inv-client-${c.id}`} onClick={() => pick(c)}>{c.first_name} {c.last_name}</button>)}</div>}
          </Field>
          {["name", "email", "address", "tax_code"].map((k) => (
            <Field key={k} label={{ name: "Customer name", email: "Email", address: "Address", tax_code: "Tax code / VAT no." }[k]}>
              <Input data-testid={`inv-buyer-${k}`} value={f.buyer[k]} onChange={(e) => setF({ ...f, buyer: { ...f.buyer, [k]: e.target.value } })} />
            </Field>
          ))}
        </div>
        <div className="space-y-2">
          <p className="text-sm font-semibold">Lines (prices include VAT)</p>
          {f.lines.map((l, i) => <LineRow key={i} l={l} i={i} onChange={setLine} onRemove={(j) => setF({ ...f, lines: f.lines.filter((_, k) => k !== j) })} />)}
          <Button size="sm" variant="outline" data-testid="inv-add-line" onClick={() => setF({ ...f, lines: [...f.lines, { ...BLANK_LINE }] })}><Plus className="h-4 w-4 mr-1" />Add line</Button>
        </div>
        <Field label="Notes"><Input data-testid="inv-notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field>
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="inv-paid" checked={f.paid} onChange={(e) => setF({ ...f, paid: e.target.checked })} />Already paid</label>
          {f.paid && <NativeSelect testId="inv-method" value={f.method} onChange={(v) => setF({ ...f, method: v })} options={METHODS.map((m) => ({ value: m, label: m.replace("_", " ") }))} />}
          <span className="ml-auto font-semibold" data-testid="inv-total">Total {total.toFixed(2)}</span>
          <Button className="rounded-full" data-testid="inv-save" onClick={save}>Create invoice</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
